import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X } from 'lucide-react';
import TableChatPicker from 'components/table/TableChatPicker';
import { useTableLayoutMode } from 'hooks/useTableLayoutMode';

interface HeroChatPopoverProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSendPhrase: (phraseId: string) => void;
  onSendSticker: (stickerId: string) => void;
}

function desktopPopoverPos(trigger: HTMLElement) {
  const rect = trigger.getBoundingClientRect();
  const panelWidth = Math.min(320, window.innerWidth - 16);
  const left = Math.min(
    Math.max(panelWidth / 2 + 8, rect.left + rect.width / 2),
    window.innerWidth - panelWidth / 2 - 8
  );
  return {
    left,
    bottom: Math.max(12, window.innerHeight - rect.top + 12),
  };
}

export default function HeroChatPopover({
  open,
  onToggle,
  onClose,
  onSendPhrase,
  onSendSticker,
}: HeroChatPopoverProps) {
  const { mode } = useTableLayoutMode();
  const isMobile = mode !== 'desktop';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(0);
  const [popoverPos, setPopoverPos] = useState<{ left: number; bottom: number } | null>(null);

  const updatePopoverPos = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || isMobile) return;
    setPopoverPos(desktopPopoverPos(trigger));
  }, [isMobile]);

  useLayoutEffect(() => {
    if (!open || isMobile) {
      if (!open) setPopoverPos(null);
      return;
    }
    updatePopoverPos();
  }, [open, isMobile, updatePopoverPos]);

  useEffect(() => {
    if (!open || isMobile) return;
    window.addEventListener('resize', updatePopoverPos);
    window.addEventListener('scroll', updatePopoverPos, true);
    return () => {
      window.removeEventListener('resize', updatePopoverPos);
      window.removeEventListener('scroll', updatePopoverPos, true);
    };
  }, [open, isMobile, updatePopoverPos]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (Date.now() - openedAtRef.current < 400) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown, { passive: true });
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, onClose]);

  const sheetClass =
    mode === 'mobile-landscape'
      ? 'tp-hero-chat-popover-mobile-landscape'
      : 'tp-hero-chat-popover-mobile-portrait';

  const popover =
    open
      ? createPortal(
          <>
            <button
              type="button"
              className="tp-hero-chat-backdrop"
              aria-label="Close chat"
              onClick={onClose}
            />
            <div
              ref={panelRef}
              className={`tp-hero-chat-popover tp-hero-chat-popover-portal ${
                isMobile ? `tp-hero-chat-popover-sheet ${sheetClass}` : 'tp-hero-chat-popover-desktop'
              }`}
              role="dialog"
              aria-label="Table chat"
              style={
                !isMobile
                  ? {
                      left: popoverPos?.left ?? '50%',
                      bottom: popoverPos?.bottom ?? 120,
                    }
                  : undefined
              }
            >
              {isMobile && <div className="tp-hero-chat-popover-handle" aria-hidden />}
              <div className="tp-hero-chat-popover-head">
                <span>Quick chat</span>
                <button type="button" className="tp-hero-chat-popover-close" onClick={onClose} aria-label="Close chat">
                  <X size={16} />
                </button>
              </div>
              <TableChatPicker
                compact
                onSendPhrase={onSendPhrase}
                onSendSticker={onSendSticker}
                onSent={onClose}
              />
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div className="tp-hero-chat-control">
      <button
        ref={triggerRef}
        type="button"
        className={`tp-seat-chat-trigger ${open ? 'tp-seat-chat-trigger-active' : ''}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!open && !isMobile && triggerRef.current) {
            setPopoverPos(desktopPopoverPos(triggerRef.current));
          }
          openedAtRef.current = Date.now();
          onToggle();
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        aria-label="Table chat"
        aria-expanded={open}
      >
        <MessageCircle size={18} strokeWidth={2.2} />
      </button>
      {popover}
    </div>
  );
}
